import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { PageHeader } from '@components/page-header';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@components/ui/card';
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent
} from '@components/ui/chart';
import { Api } from '@core/api';
import { DailyActiveUsersDayDto } from '@core/api/MobileClient';
import { injectTSDI } from '@core/tsdi';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { Spinner } from '@components/spinner';

const chartConfig: ChartConfig = {
    userCount: { label: 'Move Republic', color: 'var(--chart-1)' }
};

const toDate = (day = '') => {
    const [year, month, date] = day.split('-').map(Number);

    return new Date(year, month - 1, date);
};

const formatDay = (day?: string, options?: Intl.DateTimeFormatOptions) =>
    toDate(day).toLocaleDateString('en-GB', options);

const formatShortDay = (day?: string) =>
    formatDay(day, { day: 'numeric', month: 'short' });

const formatLongDay = (day?: string) =>
    formatDay(day, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });

const formatCount = (count = 0) => count.toLocaleString('en-GB');

interface StatCardProps {
    title: string;
    day?: DailyActiveUsersDayDto;
}

const StatCard = ({ title, day }: StatCardProps) => (
    <Card>
        <CardHeader>
            <CardDescription>{title}</CardDescription>
            <CardTitle className="text-4xl tabular-nums">
                {day ? formatCount(day.userCount) : '–'}
            </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
            {day ? formatLongDay(day.day) : 'No finished day yet'}
        </CardContent>
    </Card>
);

export const Dashboard = observer(() => {
    const api = injectTSDI(Api);
    const overview = useWrapRequest(
        async () => {
            const result =
                await api.mobileClient.supportApi.dailyActiveUsersSupportControllerGetOverview();

            return result.data;
        },
        { deps: [] }
    );

    return (
        <>
            <PageHeader
                title="Daily active users"
                description="Users who opened the Move Republic mobile app, counted on the day their app reported. A day is final around 12:00 UTC the next day."
            />
            {overview.match({
                loading: () => (
                    <div className="flex justify-center py-12">
                        <Spinner />
                    </div>
                ),
                error: (error) => (
                    <Alert variant="destructive">
                        <AlertTitle>
                            Error loading daily active users
                        </AlertTitle>
                        <AlertDescription>{error.message}</AlertDescription>
                    </Alert>
                ),
                fetched: ({ days = [], record }) => (
                    <div className="grid gap-4">
                        <div className="grid gap-4 md:grid-cols-3">
                            <StatCard
                                title="Today so far"
                                day={days[days.length - 1]}
                            />
                            <StatCard
                                title="Yesterday"
                                day={days[days.length - 2]}
                            />
                            <StatCard title="All-time record" day={record} />
                        </div>
                        <Card>
                            <CardHeader>
                                <CardTitle>Last 28 days</CardTitle>
                                <CardDescription>
                                    The last point is today and still counting.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ChartContainer
                                    config={chartConfig}
                                    className="h-[360px] w-full"
                                >
                                    <LineChart data={toJS(days)}>
                                        <CartesianGrid vertical={false} />
                                        <XAxis
                                            dataKey="day"
                                            tickFormatter={formatShortDay}
                                            tickLine={false}
                                            axisLine={false}
                                            minTickGap={24}
                                        />
                                        <YAxis
                                            allowDecimals={false}
                                            tickFormatter={formatCount}
                                            tickLine={false}
                                            axisLine={false}
                                            width={48}
                                        />
                                        <ChartTooltip
                                            content={
                                                <ChartTooltipContent
                                                    labelFormatter={(day) =>
                                                        formatLongDay(
                                                            String(day)
                                                        )
                                                    }
                                                />
                                            }
                                        />
                                        <Line
                                            dataKey="userCount"
                                            stroke="var(--color-userCount)"
                                            strokeWidth={2}
                                            dot={false}
                                        />
                                    </LineChart>
                                </ChartContainer>
                            </CardContent>
                        </Card>
                    </div>
                )
            })}
        </>
    );
});
